import {IconButton} from '@dropbox/dig-components/buttons';
import {IconButtonGroup} from '@dropbox/dig-components/combinations';
import {List} from '@dropbox/dig-components/list';
import {Text} from '@dropbox/dig-components/typography';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine, DeleteLine, TeamLine} from '@dropbox/dig-icons/assets';
import {Employee, EmployeeLdap, Team, TeamWithCounts} from 'client';
import {Avatar, AvatarIcon} from 'components/DSYS/Avatar';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {TeamMembersAndSubTeamsCounts} from 'components/shared/TeamMembersAndSubTeamsCounts';
import {useTeam} from 'components/teams/hooks';
import {sortEmployees, sortTeams} from 'helpers/utils';
import {getRoleLabel, isValidRole} from 'helpers/workstream_roles';
import {isEmployee, isTeam} from 'hooks/useEmployee';
import {ReactNode, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

export const WorkstreamPrivacyList = ({
  canDelete = false,
  selectedEmployeesOrTeams,
  onRemoveEmployeeOrTeam,
  owners,
  delegates,
  reportingLine,
}: {
  canDelete?: boolean;
  selectedEmployeesOrTeams: (EmployeeLdap | TeamWithCounts)[];
  onRemoveEmployeeOrTeam?: (employee: EmployeeLdap | TeamWithCounts) => void;
  owners?: (EmployeeLdap & {dri: string})[];
  delegates?: EmployeeLdap[];
  reportingLine?: EmployeeLdap[];
}) => {
  return (
    <List
      isSelectable
      className={atoms({
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      {selectedEmployeesOrTeams
        .filter(isTeam)
        .sort(sortTeams)
        .map((item: Team) => (
          <TeamPrivacyItem
            key={`team-${item.dbx_team_id}`}
            item={item}
            onRemoveEmployeeOrTeam={onRemoveEmployeeOrTeam!}
            canDelete={canDelete}
          />
        ))}
      {selectedEmployeesOrTeams
        .filter(isEmployee)
        .sort(sortEmployees)
        .map((item: Employee) => (
          <EmployeePrivacyItem
            key={`employee-${item.user_id}`}
            item={item}
            onRemoveEmployeeOrTeam={onRemoveEmployeeOrTeam!}
            canDelete={canDelete}
          />
        ))}
      {delegates?.map((delegate) => (
        <CustomListItem
          key={`delegate-${delegate.user_id}`}
          itemInfo={{type: 'delegate', delegate}}
          canDelete={canDelete}
        />
      ))}
      {reportingLine?.map((manager) => (
        <CustomListItem
          key={`reporting-line-${manager.user_id}`}
          itemInfo={{type: 'reportingLine', manager}}
          canDelete={canDelete}
        />
      ))}
      {owners?.map((owner) => (
        <CustomListItem
          key={`owner-${owner.user_id}`}
          itemInfo={{type: 'owner', dri: owner.dri, goalOwner: owner}}
          canDelete={canDelete}
        />
      ))}
    </List>
  );
};

const TeamPrivacyItem = ({
  canDelete = false,
  item,
  onRemoveEmployeeOrTeam,
}: {
  canDelete?: boolean;
  item: TeamWithCounts;
  onRemoveEmployeeOrTeam: (item: EmployeeLdap | Team) => void;
}) => {
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();

  const team = useTeam({slug: item.slug ?? ''});

  const membersCount: number | null =
    (team ? team.total_employee_count : item.members_count) ?? null;
  const subTeamsCount: number | null = (team ? team.children?.length : item.subteams_count) ?? null;

  const onNavigate = () => {
    navigate(`/teams/${item.slug}`);
  };

  return (
    <List.Item
      gap="small"
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: '8px',
        borderRadius: '8px',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={canDelete ? () => {} : onNavigate}
    >
      <AvatarIcon src={TeamLine} size="medium" />
      <List.Content>
        <LabelGroup
          withText={item.name}
          withSubtext={
            membersCount !== null &&
            subTeamsCount !== null && (
              <TeamMembersAndSubTeamsCounts
                membersCount={membersCount}
                subTeamsCount={subTeamsCount}
              />
            )
          }
        />
      </List.Content>
      <List.Accessory>
        {hover && (
          <IconButtonGroup>
            {({getButtonProps}) => (
              <>
                <IconButton
                  variant="transparent"
                  {...getButtonProps()}
                  shape="circular"
                  onClick={canDelete ? () => {} : onNavigate}
                  aria-label="icon button"
                >
                  {canDelete ? (
                    <UIIcon
                      src={DeleteLine}
                      style={{color: 'var(--dig-color__border__base)'}}
                      onClick={() => onRemoveEmployeeOrTeam(item)}
                    />
                  ) : (
                    <UIIcon
                      src={ChevronRightLine}
                      style={{color: 'var(--dig-color__border__base)'}}
                      onClick={onNavigate}
                    />
                  )}
                </IconButton>
              </>
            )}
          </IconButtonGroup>
        )}
      </List.Accessory>
    </List.Item>
  );
};

const EmployeePrivacyItem = ({
  canDelete = false,
  item,
  onRemoveEmployeeOrTeam,
}: {
  canDelete?: boolean;
  item: Employee;
  onRemoveEmployeeOrTeam: (item: Employee | Team) => void;
}) => {
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();

  const onNavigate = () => {
    navigate(`/people/${item.ldap}`);
  };

  return (
    <List.Item
      gap="small"
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: '4px',
        borderRadius: '8px',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={canDelete ? () => {} : onNavigate}
    >
      <List.Accessory style={{width: '32px', height: '32px'}}>
        <Avatar user={item} isInactive={item.is_deactivated ?? false} />
      </List.Accessory>
      <List.Content>
        <PersonHoverTrigger employee={item}>
          <LabelGroup withText={item.name} />
        </PersonHoverTrigger>
      </List.Content>
      <List.Accessory>
        {hover && (
          <IconButtonGroup>
            {({getButtonProps}) => (
              <>
                <IconButton
                  variant="transparent"
                  {...getButtonProps()}
                  shape="circular"
                  onClick={canDelete ? () => {} : onNavigate}
                  aria-label="icon button"
                >
                  {canDelete ? (
                    <UIIcon
                      src={DeleteLine}
                      style={{color: 'var(--dig-color__border__base)'}}
                      onClick={() => onRemoveEmployeeOrTeam(item)}
                    />
                  ) : (
                    <UIIcon
                      src={ChevronRightLine}
                      style={{color: 'var(--dig-color__border__base)'}}
                      onClick={onNavigate}
                    />
                  )}
                </IconButton>
              </>
            )}
          </IconButtonGroup>
        )}
      </List.Accessory>
    </List.Item>
  );
};

const CustomListItem = ({
  itemInfo,
  canDelete,
}: {
  itemInfo:
    | {
        type: 'contributor';
        contributor: EmployeeLdap;
      }
    | {
        type: 'owner';
        dri: string;
        goalOwner: EmployeeLdap;
      }
    | {
        type: 'reportingLine';
        manager: EmployeeLdap;
      }
    | {
        type: 'delegate';
        delegate: EmployeeLdap;
      };
  canDelete: boolean;
}) => {
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();
  const {t} = useTranslation();
  const itemLdap =
    itemInfo.type === 'contributor'
      ? itemInfo.contributor.ldap
      : itemInfo.type === 'owner'
        ? itemInfo.goalOwner.ldap
        : itemInfo.type === 'reportingLine'
          ? itemInfo.manager.ldap
          : itemInfo.type === 'delegate'
            ? itemInfo.delegate.ldap
            : undefined;
  const itemLabel =
    itemInfo.type === 'contributor'
      ? t('co-owner')
      : itemInfo.type === 'owner'
        ? isValidRole(itemInfo.dri)
          ? getRoleLabel(itemInfo.dri)
          : t(itemInfo.dri)
        : itemInfo.type === 'reportingLine'
          ? t('upwards_reporting_line')
          : itemInfo.type === 'delegate'
            ? t('delegate')
            : undefined;

  const enableNavigation = !canDelete && itemLdap;
  const onNavigate = enableNavigation ? () => navigate(`/people/${itemLdap}`) : undefined;
  const itemEmployee =
    itemInfo.type === 'contributor'
      ? itemInfo.contributor
      : itemInfo.type === 'owner'
        ? itemInfo.goalOwner
        : itemInfo.type === 'reportingLine'
          ? itemInfo.manager
          : itemInfo.type === 'delegate'
            ? itemInfo.delegate
            : undefined;

  return (
    <List.Item
      isSelectable={!!enableNavigation}
      gap="small"
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: '4px',
        borderRadius: '8px',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onNavigate}
    >
      <List.Accessory style={{height: '32px', width: '32px'}}>
        {itemInfo.type === 'owner' && (
          <Avatar
            user={itemInfo.goalOwner}
            isInactive={itemInfo.goalOwner.is_deactivated ?? false}
          />
        )}

        {itemInfo.type === 'reportingLine' && (
          <Avatar user={itemInfo.manager} isInactive={itemInfo.manager.is_deactivated ?? false} />
        )}
        {itemInfo.type === 'delegate' && (
          <Avatar user={itemInfo.delegate} isInactive={itemInfo.delegate.is_deactivated ?? false} />
        )}
        {itemInfo.type === 'contributor' && (
          <Avatar
            user={itemInfo.contributor}
            isInactive={itemInfo.contributor.is_deactivated ?? false}
          />
        )}
      </List.Accessory>
      <List.Content>
        {itemEmployee && (
          <PersonHoverTrigger employee={itemEmployee}>
            <LabelGroup withText={itemEmployee.name} />
          </PersonHoverTrigger>
        )}
      </List.Content>
      <List.Accessory>
        {hover && enableNavigation && (
          <IconButtonGroup>
            {({getButtonProps}) => (
              <>
                <IconButton
                  variant="transparent"
                  {...getButtonProps()}
                  shape="circular"
                  onClick={onNavigate}
                  aria-label="icon button"
                >
                  <UIIcon
                    src={ChevronRightLine}
                    style={{color: 'var(--dig-color__border__base)'}}
                    onClick={onNavigate}
                  />
                </IconButton>
              </>
            )}
          </IconButtonGroup>
        )}
        {(!hover || !enableNavigation) && itemLabel && (
          <Text color="faint" size="small" tagName="label">
            {itemLabel}
          </Text>
        )}
      </List.Accessory>
    </List.Item>
  );
};

const PersonHoverTrigger = ({
  children,
  employee,
}: {
  children: ReactNode;
  employee: EmployeeLdap;
}) => (
  <PersonHoverCard ldap={employee.ldap} name={employee.name}>
    <span style={{display: 'inline-flex', minWidth: 0}}>{children}</span>
  </PersonHoverCard>
);
